/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { useBlockProps } from '@wordpress/block-editor';

/**
 * The save function defines the way in which the different attributes should
 * be combined into the final markup, which is then serialized by the block
 * editor into `post_content`.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#save
 *
 * @return {WPElement} Element to render.
 */
export default function save({ attributes }) {

	let aspect_ratio_width, aspect_ratio_height;
	let img_srcset;
	let img_sizes;
	let img_alt = 'Image With Benefits';

	// add cache-bust timestamp
	const img_src = attributes.src.replace('/view', ('/cv:'+attributes.cache_ver).substring(0,14) + '/view');

	// aspect-ratio to stop content-shift...
	// mostly! if variants are different aspect ratios it may shift
	Object.entries(attributes.variants).forEach(function([variant_id, variant]) {
		if (attributes.lock_aspect_ratio) {
			if (variant.variant_type == 'primary') {
				aspect_ratio_width = variant.width;
				aspect_ratio_height = variant.height;
			}

		} else if (variant.variant_type != 'baseline') {
			// smallest aspect ratio
			if (typeof aspect_ratio_width == 'undefined' || (variant.width/variant.height) > (aspect_ratio_width/aspect_ratio_height)) {
				aspect_ratio_width = variant.width;
				aspect_ratio_height = variant.height;
			}
		}

		if (variant.variant_type == 'primary') {
			if (variant.srcset_widths) {
				img_srcset = variant.srcset_widths.map(function(_width) {
					return img_src.replace('/view', '/w:' + _width + '/view ' + _width + 'w');
				}).join(', ')

				const max_width = Math.max(...variant.srcset_widths);
				img_sizes = '(max-width: ' + max_width + 'px) 100vw, ' + max_width + 'px';
			}
		}

		// and baseline without svg support gets an alt. svg images use title to display label
		if (variant.variant_type == 'baseline') {
			img_alt = variant.label;
		}
	});

	function getImgProps() {
		return {
			src: img_src,
			srcset: img_srcset,
			sizes: img_sizes,
			loading: (attributes.loading_speed == 'lazy' ? 'lazy' : 'eager'),
			// signal other lazy loaders to leave alone
			className: (attributes.loading_speed == 'lazy' ? null : 'imgwb-eager'),
			alt: img_alt,
			title: img_alt,
			style: 'width:100%;height:auto;display:block'
		};
	}

	function getImgLinkProps() {
		let rv = {
			href: attributes.link
		}

		// _blank
		if (attributes.link_blank) {
			rv.target = '_blank'
		}

		return rv;
	}

	function getFigureProps() {
		let figure_style = {};

		figure_style.aspectRatio = aspect_ratio_width + '/' + aspect_ratio_height;

		if (attributes.loading_speed == 'instant' && attributes.instant_load_src) {
			//figure_style.background = 'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHg9IjEwMCIgeT0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PGNpcmNsZSBjeD0iNTAiIGN5PSI1MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZWVlIiBzdHJva2Utd2lkdGg9IjEwIiByPSIzNSIgc3Ryb2tlLWRhc2hhcnJheT0iMTUwIDYwIj48YW5pbWF0ZVRyYW5zZm9ybSBhdHRyaWJ1dGVOYW1lPSJ0cmFuc2Zvcm0iIHR5cGU9InJvdGF0ZSIgZHVyPSIxcyIgZnJvbT0iMCA1MCA1MCIgdG89IjM2MCA1MCA1MCIgcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiPjwvYW5pbWF0ZVRyYW5zZm9ybT48L2NpcmNsZT48L3N2Zz4=") center no-repeat,url("' + attributes.instant_load_src + '") center';
			//figure_style.backgroundSize = '20%,cover';

			// without spinner
			figure_style.background = 'url("' + attributes.instant_load_src + '") center';
			figure_style.backgroundSize = 'cover';
		}

		return useBlockProps.save({
			className: 'wp-block-image',
			style: figure_style
		});
	}

	let imgwb = <img { ...getImgProps() }/>;

	if (attributes.link) {
		imgwb = <a { ...getImgLinkProps() }>{ imgwb }</a>;
	}

	return (
		<figure { ...getFigureProps() }>{ imgwb }</figure>
	);
}
